import React, { useState, useEffect } from 'react';
import { UserPlus } from 'lucide-react';
import { DEFAULT_CUSTOMIZATION, ProfileCustom, ProfileAnimation, ColorFilter, AuraMode, Texture } from './core/identity/ProfileTypes';
import { IdentityRenderer } from './core/identity/IdentityRenderer';
import { FONT_FAMILIES, getNameStyle } from './core/identity/styles';
import { getTextureOverlay, clampOpacity, clampIntensity } from './core/identity/textures';
import { useLFO } from './hooks/useLFO';
import { useAudioReactive } from './hooks/useAudioReactive';
import { supabase } from './supabase';

interface ProfilePreviewProps {
  profile: any;
  currentVaultId?: string | null;
  onAvatarClick?: () => void;
  onBannerClick?: () => void;
  disableInitialAnimation?: boolean;
  onAddFriend?: () => void;
  isFriend?: boolean;
  onSendDM?: () => void;
  isSelf?: boolean;
}

const PALETTE_GRADIENTS: Record<string, string> = {
  "Cyberpunk": "linear-gradient(135deg, #0f0021 0%, #1a0040 50%, #0a1a3a 100%)",
  "Vaporwave": "linear-gradient(135deg, #1a0040 0%, #3a0060 50%, #0a2060 100%)",
  "Outrun": "linear-gradient(135deg, #0a0a1a 0%, #1a0a2a 50%, #2a0a1a 100%)",
  "Neon Nights": "linear-gradient(135deg, #0a0a14 0%, #0a1428 50%, #140a28 100%)",
  "Midnight": "linear-gradient(135deg, #060612 0%, #0c0c1e 50%, #06060e 100%)",
  "Enchanted Forest": "linear-gradient(135deg, #061f12 0%, #0b3a24 50%, #1c320c 100%)",
  "Celestial Stardust": "linear-gradient(135deg, #14051c 0%, #290b3a 50%, #401230 100%)",
  "Ethereal Light": "linear-gradient(135deg, #f7f5f0 0%, #ebdcb9 50%, #dfc893 100%)",
  "Fairy Grove": "linear-gradient(135deg, #0f2c20 0%, #2b1f3d 50%, #173b2c 100%)",
  "Abyssal Deep": "linear-gradient(135deg, #020a16 0%, #061c3a 50%, #0b3252 100%)",
};

export default function ProfilePreview({ profile, currentVaultId, onAvatarClick, onBannerClick, disableInitialAnimation, onAddFriend, isFriend, onSendDM, isSelf }: ProfilePreviewProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [serverRoles, setServerRoles] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Fetch real server roles for this user from the DB
  useEffect(() => {
    if (!profile?.id || profile.id === 'me') {
      setServerRoles([]);
      return;
    }
    let cancelled = false;
    setRolesLoading(true);
    supabase
      .from('server_member_roles')
      .select(`
        role_id,
        server_roles!inner(
          id, name, color, icon, position, role_kind, permission_ids, vault_id
        )
      `)
      .eq('user_id', profile.id)
      .then(({ data, error }) => {
        if (cancelled) return;
        setRolesLoading(false);
        if (error) {
          console.warn('Could not fetch server roles for profile:', error.message);
          return;
        }
        const roles = (data ?? [])
          .map((r: any) => r.server_roles)
          .filter((r: any) => {
            if (!r) return false;
            // Hide context-specific server permission roles when viewing profile outside that server context
            if (!currentVaultId && r.role_kind === 'permission') return false;
            if (currentVaultId && r.vault_id && r.vault_id !== currentVaultId) return false;
            return true;
          })
          .sort((a: any, b: any) => (a.position ?? 999) - (b.position ?? 999));
        setServerRoles(roles);
      });
    return () => { cancelled = true; };
  }, [profile?.id, currentVaultId]);


  // Play animation automatically when first opening/viewing user profile
  React.useEffect(() => {
    if (disableInitialAnimation) return;
    setIsAnimating(true);
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [profile.id, disableInitialAnimation]);

  const handleBannerClick = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
    }, 1500);
  };

  const customization: ProfileCustom = {
    ...DEFAULT_CUSTOMIZATION,
    ...(profile.customization || {})
  };

  const fontFamily = FONT_FAMILIES[customization.bodyFont || customization.font || 'Inter'] || FONT_FAMILIES.Inter;
  const nameStyleCSS = getNameStyle(customization.nameStyle, customization.color1, customization.color2, customization.gradientDirection);
  const tc1 = customization.themeColor1 || customization.color1 || '#818cf8';
  const tc2 = customization.themeColor2 || customization.color2 || '#22d3ee';

  // Custom Backdrop Logic
  let paletteBg = PALETTE_GRADIENTS[customization.palette];
  if (customization.palette === "Custom Solid") {
    paletteBg = customization.customBgColor || '#090d16';
  } else if (customization.palette === "Custom Gradient") {
    paletteBg = `linear-gradient(135deg, ${tc1}40 0%, ${tc2}20 100%)`;
  }

  // ---- Section 5: LFO + Synthetic Audio Amplitude source ----
  const lfoValue = useLFO(customization.lfoWaveform, customization.lfoRate, customization.lfoFrequency);
  const audioAmp = useAudioReactive(customization.auraMode === 'Audio-Reactive', customization.audioGain);

  // Texture overlay recipe — pulled from the centralized `textures.ts`
  // helper so any future additions stay in one place. We layer the
  // user-controlled `textureOpacity` (0..100 -> 0..1) on top of the
  // recipe's intrinsic alpha (e.g., CRT Scanlines are darker than
  // VCR Static).
  const textureRecipe = getTextureOverlay(customization.texture as Texture);
  const textureOpacityApplied = textureRecipe
    ? (clampOpacity(customization.textureOpacity) *
        (textureRecipe.baseOpacity ?? 1))
    : 0;
  const overlayStyle: React.CSSProperties = textureRecipe
    ? {
        background: textureRecipe.background,
        backgroundSize: textureRecipe.backgroundSize,
        mixBlendMode: textureRecipe.mixBlendMode,
        animation: textureRecipe.animation,
        opacity: textureOpacityApplied,
      }
    : {};
  // Intensity slider scales the texture animation duration. Bumping it
  // tightens the loop period so the texture "feels" more alive. LFO Rate
  // (from the separate Modulators panel) is a second, independent speed
  // multiplier — this is what makes "Modulators drive the texture speed"
  // actually true instead of Rate/Frequency only ever touching the color
  // filter below.
  const intensityMul = clampIntensity(customization.textureIntensity);
  const lfoSpeedMul = Math.max(0.1, customization.lfoRate ?? 1.0);
  const overlayStyleWithIntensity: React.CSSProperties = textureRecipe?.animation
    ? { ...overlayStyle, animationDuration: `${Math.max(0.3, 8 / (intensityMul * lfoSpeedMul))}s` }
    : overlayStyle;

  // Neon light sweep — an independent animated diagonal band layered over
  // the texture/banner, tinted by the aura color pair. Cheap: pure
  // background-position animation on a single gradient layer, no filters.
  const neonSweepStyle: React.CSSProperties | null = customization.textureNeonSweep
    ? {
        background: `linear-gradient(100deg, transparent 42%, ${customization.auraColor1 || '#22d3ee'}70 50%, transparent 58%)`,
        backgroundSize: '300% 100%',
        mixBlendMode: 'screen',
        animation: `texture-neon-sweep ${Math.max(1.2, 5 / lfoSpeedMul)}s linear infinite`,
      }
    : null;

  // ---- Section 5: Color modulation container (LFO-driven filters) ----
  // We compute one inline `filter` string consumed by the inner wrapper.
  // The hue-rotate angle is the LFO in [-1, 1] mapped to [-180, 180]
  // degrees, multiplied by a 30deg base so even a sliding 50% LFO feels
  // noticeable without overwhelming.
  const lfoAngleDeg = lfoValue * 180;
  const colorFilterFn: ColorFilter = (customization.colorFilter as ColorFilter) ?? 'None';
  const modulationFilter = (() => {
    if (colorFilterFn === 'None' && (customization.lfoWaveform ?? 'None') === 'None') return undefined;
    switch (colorFilterFn) {
      case 'HSV Inversion':
        return `hue-rotate(${lfoAngleDeg}deg) saturate(${1 + Math.abs(lfoValue) * 0.6})`;
      case 'Channel Shift': {
        const r = Math.max(0,  Math.round(lfoValue * 4));
        const b = Math.max(0, -Math.round(lfoValue * 4));
        // CSS doesn't expose a per-channel shift directly; approximate
        // with a small offset + hue rotation, so the channel "shifts"
        // visually without going through a canvas filter pipeline.
        return `hue-rotate(${lfoAngleDeg * 0.3}deg) contrast(${1 + Math.abs(lfoValue) * 0.25}) drop-shadow(${r}px 0 0 rgba(255,0,128,0.35)) drop-shadow(${-b}px 0 0 rgba(0,240,255,0.35))`;
      }
      case 'Psychodynamic Red':
        return `saturate(${1.4 + Math.abs(lfoValue) * 0.6}) hue-rotate(${lfoAngleDeg * 0.25}deg) drop-shadow(0 0 ${4 + Math.abs(lfoValue) * 6}px rgba(255,16,32,0.45))`;
      default:
        // No filter selected, but LFO is active — apply gentle hue sweep.
        return `hue-rotate(${lfoAngleDeg * 0.4}deg)`;
    }
  })();

  // For light themes (like Ethereal Light), adapt text colors beautifully
  const isLightPalette = customization.palette === "Ethereal Light";
  const defaultTextColor = isLightPalette ? "#1a1610" : "white";
  const mutedTextColor = isLightPalette ? "rgba(26, 22, 16, 0.6)" : "rgba(255, 255, 255, 0.6)";

  const avatarBorder = customization.avatarDecoration === "None" ? `4px solid ${isLightPalette ? '#dfc893' : '#05070a'}`
    : `4px solid ${tc1}`;

  // ---- Section 5: Expanded Aura Modes ----
  // Modes we render: None, Static, Pulse Animated, Breathe, RGB Color-Cycle,
  // Audio-Reactive, Chaotic. RGB-Cycle / Audio-Reactive / Chaotic are new.
  const auraMode: AuraMode = customization.auraMode ?? 'None';
  const auraKey = `${auraMode}:${customization.auraColor1}:${customization.auraColor2}`;

  const auraStyle: React.CSSProperties = auraMode !== "None" ? {
    boxShadow: auraMode === 'Static'
      ? `0 0 30px ${customization.auraColor1}60, 0 0 60px ${customization.auraColor2}30, inset 0 0 30px ${customization.auraColor1}10`
      : auraMode === 'RGB Color-Cycle' && lfoValue !== undefined
        // Don't shadow on RGB-Cycle (too many colors), keep the hue on the inset
        ? `inset 0 0 20px ${customization.auraColor1}10`
        : auraMode === 'Audio-Reactive'
          ? `0 0 ${10 + audioAmp * 60}px ${customization.auraColor1}${Math.round(40 + audioAmp * 200).toString(16).padStart(2,'0')}, 0 0 ${20 + audioAmp * 90}px ${customization.auraColor2}40, inset 0 0 ${10 + audioAmp * 25}px ${customization.auraColor1}20`
          : undefined,
    animation:
      auraMode === 'Pulse Animated' ? 'aura-pulse 2s ease-in-out infinite'
      : auraMode === 'Breathe' ? 'aura-breathe 4s ease-in-out infinite'
      : auraMode === 'RGB Color-Cycle'
        ? `aura-rgb-cycle ${Math.max(2, 8 / Math.max(0.25, customization.rgbCycleSpeed ?? 1))}s linear infinite`
      : auraMode === 'Chaotic'
        ? `aura-chaotic ${Math.max(0.4, 1.4 / Math.max(0.5, customization.chaoticSpeed ?? 1))}s steps(8) infinite`
      : 'none'
  } : {};
  void auraKey; // reserved for future per-frame re-render hooks

  // Custom styling for container card
  const containerStyle: React.CSSProperties = {
    fontFamily,
    background: paletteBg,
    color: defaultTextColor,
    ...auraStyle
  };

  return (
    <div
      onClick={handleBannerClick}
      className={`neural-glass rounded-3xl overflow-hidden flex flex-col w-full max-w-2xl mx-auto mb-6 shadow-2xl relative transition-all duration-500 border border-white/5 ${isAnimating && customization.profileAnimation !== 'None' ? `anim-${(customization.profileAnimation || 'Glitch').replace(' ', '-')}` : ''}`}
      style={containerStyle}
    >
      <style>{`
        @keyframes aura-pulse {
          0%, 100% {
            box-shadow: 0 0 15px ${customization.auraColor1}50, 0 0 30px ${customization.auraColor2}25, inset 0 0 15px ${customization.auraColor1}10;
          }
          50% {
            box-shadow: 0 0 35px ${customization.auraColor1}b0, 0 0 70px ${customization.auraColor2}60, inset 0 0 35px ${customization.auraColor1}30;
          }
        }
        @keyframes aura-breathe {
          0%, 100% {
            box-shadow: 0 0 10px ${customization.auraColor1}40, 0 0 20px ${customization.auraColor2}20, inset 0 0 10px ${customization.auraColor1}08;
          }
          50% {
            box-shadow: 0 0 40px ${customization.auraColor1}c0, 0 0 80px ${customization.auraColor2}70, inset 0 0 40px ${customization.auraColor1}35;
          }
        }
        /* Section 5: RGB Color-Cycle aura — cycle the inset hue through the rainbow
           while the outer drop-shadow stays the user's chosen color pair.
           Hue stops are normalized to [0, 360) so interpolation stays clean
           across browsers. */
        @keyframes aura-rgb-cycle {
          0%   { box-shadow: 0 0 25px hsl(0,   85%, 55%) 50, 0 0 50px hsl(60,  85%, 55%) 30, inset 0 0 25px hsl(120, 85%, 55%) 10; }
          16%  { box-shadow: 0 0 25px hsl(60,  85%, 55%) 50, 0 0 50px hsl(120, 85%, 55%) 30, inset 0 0 25px hsl(180, 85%, 55%) 10; }
          33%  { box-shadow: 0 0 25px hsl(120, 85%, 55%) 50, 0 0 50px hsl(180, 85%, 55%) 30, inset 0 0 25px hsl(240, 85%, 55%) 10; }
          50%  { box-shadow: 0 0 25px hsl(180, 85%, 55%) 50, 0 0 50px hsl(240, 85%, 55%) 30, inset 0 0 25px hsl(300, 85%, 55%) 10; }
          66%  { box-shadow: 0 0 25px hsl(240, 85%, 55%) 50, 0 0 50px hsl(300, 85%, 55%) 30, inset 0 0 25px hsl(0,   85%, 55%) 10; }
          83%  { box-shadow: 0 0 25px hsl(300, 85%, 55%) 50, 0 0 50px hsl(0,   85%, 55%) 30, inset 0 0 25px hsl(60,  85%, 55%) 10; }
          100% { box-shadow: 0 0 25px hsl(0,   85%, 55%) 50, 0 0 50px hsl(60,  85%, 55%) 30, inset 0 0 25px hsl(120, 85%, 55%) 10; }
        }
        /* Section 5: Chaotic aura — rapid irregular flashing via stepped keyframe */
        @keyframes aura-chaotic {
          0%   { box-shadow: 0 0 10px ${customization.auraColor1}20, 0 0 20px ${customization.auraColor2}10; }
          20%  { box-shadow: 0 0 80px ${customization.auraColor1}ff, 0 0 160px ${customization.auraColor2}80; }
          30%  { box-shadow: 0 0 8px  ${customization.auraColor2}15, inset 0 0 8px ${customization.auraColor1}10; }
          45%  { box-shadow: 0 0 60px ${customization.auraColor2}c0, 0 0 130px ${customization.auraColor1}70; }
          55%  { box-shadow: 0 0 12px ${customization.auraColor1}30, 0 0 25px ${customization.auraColor2}18; }
          75%  { box-shadow: 0 0 90px ${customization.auraColor1}d0, 0 0 180px ${customization.auraColor2}90; }
          100% { box-shadow: 0 0 10px ${customization.auraColor1}20, 0 0 20px ${customization.auraColor2}10; }
        }
        /* Section 5: Texture animation helpers — used by texture recipes
           in textures.ts for things that need drifting/scaling motion. */
        @keyframes texture-pulse {
          0%, 100% { opacity: 0.6; }
          50%      { opacity: 1.0; }
        }
        @keyframes texture-drift {
          0%   { background-position: 0%   0%; }
          100% { background-position: 100% 100%; }
        }
        @keyframes texture-shift {
          0%   { background-position: 0%   0%, 0 0; }
          100% { background-position: 100% 0%, 0 0; }
        }
        /* Section 5: Neon Light Sweep — a diagonal highlight band drifting
           across the card. background-size is 300% wide so the band travels
           fully off-frame before looping, avoiding a visible jump-cut. */
        @keyframes texture-neon-sweep {
          0%   { background-position: 100% 0%; }
          100% { background-position: -100% 0%; }
        }
        @keyframes aura-banner-pulse {
          0%, 100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.7;
          }
        }
        .anim-Glitch {
          animation: glitch-shake 0.8s infinite !important;
          filter: hue-rotate(180deg) contrast(150%) saturate(120%) !important;
        }
        @keyframes cyber-surge {
          0%, 100% { filter: none; transform: translate(0); }
          10% { filter: contrast(300%) saturate(200%); transform: translate(-3px, 1px); }
          20% { filter: hue-rotate(90deg) brightness(1.5); transform: translate(2px, -2px); }
          30% { filter: contrast(250%) hue-rotate(180deg); transform: translate(-1px, 3px) skew(-2deg); }
          40% { filter: saturate(300%) brightness(0.8); transform: translate(3px, 0); }
          50% { filter: invert(15%) contrast(200%) hue-rotate(270deg); transform: translate(0, -2px) skew(3deg); }
          60% { filter: brightness(2) saturate(250%); transform: translate(-2px, 1px); }
          70% { filter: hue-rotate(45deg) contrast(180%); transform: translate(1px, -1px); }
          80% { filter: none; transform: translate(0); }
        }
        .anim-Cyber-Surge {
          animation: cyber-surge 3s ease-out 1 !important;
        }
        @keyframes neon-overload {
          0% { filter: brightness(1) saturate(1) hue-rotate(0deg); }
          15% { filter: brightness(1.8) saturate(300%) hue-rotate(60deg); }
          30% { filter: brightness(0.7) saturate(200%) hue-rotate(120deg); }
          45% { filter: brightness(2.5) saturate(400%) hue-rotate(180deg); }
          60% { filter: brightness(1.2) saturate(250%) hue-rotate(240deg); }
          75% { filter: brightness(1.5) saturate(350%) hue-rotate(300deg); }
          100% { filter: brightness(1) saturate(1) hue-rotate(360deg); }
        }
        .anim-Neon-Overload {
          animation: neon-overload 3s ease-in-out 1 !important;
        }
        @keyframes void-tear {
          0%, 100% { filter: none; transform: scale(1) skew(0); }
          15% { filter: invert(80%) contrast(200%); transform: scale(1.05, 0.95) skew(-5deg); }
          30% { filter: invert(30%) hue-rotate(180deg) saturate(200%); transform: scale(0.97, 1.04) skew(3deg); }
          50% { filter: invert(100%) contrast(300%) brightness(1.5); transform: scale(1.08, 0.92) skew(-8deg); }
          70% { filter: invert(50%) hue-rotate(90deg) saturate(300%); transform: scale(0.98, 1.02) skew(2deg); }
          85% { filter: invert(10%) contrast(150%); transform: scale(1.01) skew(-1deg); }
        }
        .anim-Void-Tear {
          animation: void-tear 3s ease-out 1 !important;
        }
        @keyframes data-corrupt {
          0%, 100% { filter: none; transform: translate(0); clip-path: none; }
          8% { filter: hue-rotate(200deg) contrast(300%); transform: translate(-4px, 2px); clip-path: inset(10% 0 60% 0); }
          16% { filter: saturate(400%) brightness(1.8); transform: translate(3px, -3px); clip-path: inset(40% 0 20% 0); }
          24% { filter: hue-rotate(90deg) invert(30%); transform: translate(-2px, 1px); clip-path: none; }
          32% { filter: contrast(250%) hue-rotate(270deg); transform: translate(4px, 0); clip-path: inset(60% 0 10% 0); }
          48% { filter: brightness(2.2) saturate(350%); transform: translate(0, 3px); clip-path: none; }
          56% { filter: hue-rotate(45deg) contrast(200%); transform: translate(-3px, -2px); clip-path: inset(20% 0 50% 0); }
          72% { filter: saturate(200%); transform: translate(1px, -1px); clip-path: none; }
        }
        .anim-Data-Corrupt {
          animation: data-corrupt 2.5s ease-out 1 !important;
        }
        @keyframes plasma-burn {
          0%, 100% { filter: none; }
          20% { filter: sepia(80%) saturate(400%) brightness(1.3) hue-rotate(10deg); }
          40% { filter: sepia(50%) saturate(300%) brightness(2) hue-rotate(30deg); }
          60% { filter: sepia(100%) saturate(500%) brightness(1.5) hue-rotate(-10deg); }
          80% { filter: sepia(30%) saturate(200%) brightness(1.8) hue-rotate(20deg); }
        }
        .anim-Plasma-Burn {
          animation: plasma-burn 3s ease-in-out 1 !important;
        }
        @keyframes hologram-flicker {
          0%, 100% { filter: none; opacity: 1; }
          5% { opacity: 0.3; filter: brightness(3) hue-rotate(180deg) saturate(0); }
          10% { opacity: 1; filter: brightness(1.5) saturate(200%) hue-rotate(170deg); }
          20% { opacity: 0.6; filter: brightness(2) hue-rotate(185deg); }
          25% { opacity: 1; filter: brightness(1) hue-rotate(175deg) saturate(250%); }
          40% { opacity: 0.4; filter: brightness(2.5) hue-rotate(180deg) saturate(0); }
          45% { opacity: 1; filter: none; }
          70% { opacity: 0.7; filter: brightness(1.8) hue-rotate(180deg) saturate(150%); }
          75% { opacity: 1; filter: none; }
        }
        .anim-Hologram-Flicker {
          animation: hologram-flicker 3s ease-out 1 !important;
        }
        @keyframes liquid-chrome {
          0%, 100% { filter: none; box-shadow: none; transform: scale(1); }
          20% { filter: contrast(180%) saturate(300%) hue-rotate(45deg); box-shadow: inset 0 0 60px rgba(200,200,255,0.8); transform: scale(1.02); }
          50% { filter: contrast(160%) saturate(150%) hue-rotate(-30deg) invert(10%); box-shadow: inset 0 0 40px rgba(255,200,255,0.6); transform: scale(0.98); }
          80% { filter: contrast(200%) saturate(400%) hue-rotate(90deg); box-shadow: inset 0 0 80px rgba(100,255,255,0.5); transform: scale(1.01); }
        }
        .anim-Liquid-Chrome {
          animation: liquid-chrome 3.5s ease-in-out 1 !important;
        }
        @keyframes vhs-nightmare {
          0%, 100% { transform: skewX(0deg) translateY(0); filter: none; }
          10% { transform: skewX(2deg); filter: hue-rotate(90deg) contrast(150%) drop-shadow(2px 0 0 red) drop-shadow(-2px 0 0 cyan); }
          20% { transform: skewX(-2deg); filter: hue-rotate(0deg) contrast(100%); }
          30% { transform: skewX(0deg); }
          60% { transform: skewX(5deg) translateY(2px); filter: saturate(300%) drop-shadow(3px 0 0 red) drop-shadow(-3px 0 0 blue) invert(20%); }
          70% { transform: skewX(-5deg) translateY(-2px); filter: saturate(300%) drop-shadow(-3px 0 0 red) drop-shadow(3px 0 0 blue) invert(10%); }
          80% { transform: skewX(2deg) translateY(1px); filter: contrast(120%) drop-shadow(1px 0 0 red); }
        }
        .anim-VHS-Nightmare {
          animation: vhs-nightmare 3s ease-in-out 1 !important;
        }
        @keyframes spectral-bloom {
          0%, 100% { filter: brightness(1) saturate(1) contrast(1); box-shadow: none; transform: scale(1); }
          30% { filter: brightness(1.5) saturate(2.5) contrast(1.2); box-shadow: 0 0 60px rgba(255,100,255,0.6), 0 0 100px rgba(100,255,255,0.4); transform: scale(1.03); }
          60% { filter: brightness(1.2) saturate(3) contrast(1.5) hue-rotate(40deg); box-shadow: 0 0 80px rgba(255,255,100,0.5), 0 0 40px rgba(255,100,255,0.3); transform: scale(1.01); }
        }
        .anim-Spectral-Bloom {
          animation: spectral-bloom 3.5s ease-in-out 1 !important;
        }
        @keyframes ethereal-aura {
          0%, 100% { filter: hue-rotate(0deg) brightness(1) drop-shadow(0 0 0 transparent); transform: translateY(0); }
          40% { filter: hue-rotate(45deg) brightness(1.5) drop-shadow(0 0 30px rgba(200,255,255,0.8)); transform: translateY(-8px); }
          70% { filter: hue-rotate(-20deg) brightness(1.3) drop-shadow(0 0 20px rgba(255,200,255,0.6)); transform: translateY(-2px); }
        }
        .anim-Ethereal-Aura {
          animation: ethereal-aura 3.5s ease-in-out 1 !important;
        }
      `}</style>

      {/* Section 5: Color Modulation wrapper.
          Lives above the background but below the content.
          When neither LFO nor a color filter is selected we leave
          filter undefined so the wrapper is transparent (no extra
          paint cost from `willChange`). */}
      <div
        className="relative z-0 flex flex-col w-full"
        style={{ filter: modulationFilter, willChange: modulationFilter ? 'filter' : 'auto' }}
      >
        {/* CRT / Static Texture Overlay (only render when a recipe exists) */}
        {textureRecipe && (
          <div className="absolute inset-0 pointer-events-none rounded-3xl" style={{ ...overlayStyleWithIntensity, zIndex: -1 }} />
        )}
        {/* Neon Light Sweep — independent of texture choice, layers over everything below it */}
        {neonSweepStyle && (
          <div className="absolute inset-0 pointer-events-none rounded-3xl overflow-hidden" style={{ ...neonSweepStyle, zIndex: -1 }} />
        )}

      <div className={`h-40 w-full shrink-0 relative z-0 ${onBannerClick ? 'cursor-pointer group/banner' : ''}`} style={{ 
        background: profile.bannerType === 'color' ? profile.bannerValue : 
                    profile.bannerType === 'gradient' ? `linear-gradient(to right, hsl(${profile.gradientHue1}, ${profile.gradientSat1}%, 50%), hsl(${profile.gradientHue2}, ${profile.gradientSat2}%, 50%))` : 
                    profile.bannerType === 'image' ? `url(${profile.bannerImage}) center/cover` : 'rgba(255,255,255,0.05)'
      }} onClick={onBannerClick}>
        {/* Banner Hover Edit Overlay */}
        {onBannerClick && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/banner:opacity-100 transition-opacity duration-300 z-10 flex flex-col items-center justify-center gap-1 pointer-events-none">
            <span className="text-lg">🖼️</span>
            <span className="text-[8px] font-black uppercase tracking-widest text-white/80">Change Banner</span>
          </div>
        )}
        {/* Aura Banner Overlay */}
        {customization.auraAffectsBanner && customization.auraMode !== "None" && (
          <div 
            className="absolute inset-0 pointer-events-none mix-blend-color-dodge transition-opacity duration-300 z-10"
            style={{
              background: `radial-gradient(circle at center, ${customization.auraColor1}45, ${customization.auraColor2}20)`,
              animation: customization.auraMode === 'Pulse Animated' ? 'aura-banner-pulse 2s ease-in-out infinite' : 
                         customization.auraMode === 'Breathe' ? 'aura-banner-pulse 4s ease-in-out infinite' : 'none'
            }}
          />
        )}
        {/* Avatar Container — previously had `transform rotate-3 hover:rotate-0`
            to give a playful 'tilted sticker' look, but it tilted clockwise
            even at rest which read as off-balance. Removed so the avatar
            sits level; the `transition-transform` is intentionally gone too
            since no animated transform remains on this element. */}
        <div 
          onClick={onAvatarClick}
          className={`absolute -bottom-12 left-6 w-24 h-24 rounded-3xl bg-slate-900 shadow-xl z-20 flex items-center justify-center ${onAvatarClick ? 'cursor-pointer group/avatar' : ''}`}
        >
          {/* Quantum Halo Ring Effect */}
          {customization.avatarDecoration === "Quantum Halo" && (
            <div 
              className="absolute inset-[-6px] rounded-3xl border-2 animate-pulse opacity-85"
              style={{ 
                borderColor: customization.color2, 
                boxShadow: `0 0 16px ${customization.color2}, inset 0 0 8px ${customization.color2}`
              }}
            />
          )}
          
          {/* Inner Image Container (which HAS overflow-hidden and the border) */}
          <div 
            className="w-full h-full rounded-3xl overflow-hidden relative z-10"
            style={{ border: avatarBorder }}
          >
            <img 
              src={profile.avatar || "/psydechat-icon-no-text.png"}
              alt="Avatar"
              style={{ objectPosition: customization.avatarPosition || '50% 50%' }}
              className="w-full h-full object-cover"
            />

            {/* Hover Edit Overlay */}
            {onAvatarClick && (
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/avatar:opacity-100 transition-opacity duration-300 z-20 flex flex-col items-center justify-center gap-1 rounded-3xl">
                <span className="text-lg">📷</span>
                <span className="text-[8px] font-black uppercase tracking-widest text-white/80">Change</span>
              </div>
            )}
          </div>
          
          {/* Animated Neon Avatar Decoration */}
          {customization.avatarDecoration === "Neon Frame" && (
            <div
              className="absolute inset-[-3px] rounded-3xl border-2 animate-pulse pointer-events-none z-20"
              style={{
                borderColor: tc1,
                boxShadow: `0 0 12px ${tc1}, 0 0 4px ${tc1}`,
              }}
            />
          )}

          {/* Cyber Glitch Frame Effect */}
          {customization.avatarDecoration === "Cyber Glitch" && (
            <>
              <div
                className="absolute inset-[-2px] rounded-3xl border border-[#ff0055] animate-pulse opacity-60 pointer-events-none z-20"
              />
              <div
                className="absolute inset-[-3px] rounded-3xl border-2 pointer-events-none z-20"
                style={{
                  borderColor: '#00f5ff',
                  boxShadow: `0 0 8px #00f5ff, 0 0 2px #ff0055`,
                }}
              />
            </>
          )}

          {/* Neural Circuit Frame Effect */}
          {customization.avatarDecoration === "Neural Circuit" && (
            <div
              className="absolute inset-[-3px] rounded-3xl border-2 border-dashed animate-pulse pointer-events-none z-20"
              style={{
                borderColor: tc1,
                boxShadow: `0 0 10px ${tc1}40`,
              }}
            />
          )}

          {/* Enchanted Elven Vines Frame Effect */}
          {customization.avatarDecoration === "Elven Vines" && (
            <div
              className="absolute inset-[-4px] rounded-3xl border-2 border-emerald-500/50 pointer-events-none z-20 flex items-center justify-center animate-pulse"
              style={{
                boxShadow: `0 0 12px ${tc1 || '#10b981'}, inset 0 0 6px ${tc1 || '#10b981'}`
              }}
            >
              <div className="absolute inset-[2px] rounded-3xl border border-amber-400/30 opacity-60" />
            </div>
          )}

          {/* Celestial Orbit Ring Frame Effect */}
          {customization.avatarDecoration === "Celestial Orbit" && (
            <div
              className="absolute inset-[-4px] rounded-3xl border border-amber-300/40 animate-pulse pointer-events-none z-20"
              style={{
                boxShadow: `0 0 10px ${customization.color2 || '#f59e0b'}`
              }}
            >
              <div className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-200 shadow-[0_0_10px_#f59e0b] animate-bounce" style={{ animationDuration: '2s' }} />
            </div>
          )}

          {/* Golden Angelic Wings Frame Effect */}
          {customization.avatarDecoration === "Angelic Wings" && (
            <div className="absolute inset-0 pointer-events-none z-20 flex justify-between items-center px-0.5">
              <span className="text-sm -ml-2 select-none animate-bounce" style={{ filter: `drop-shadow(0 0 5px ${tc1 || '#fbbf24'})`, animationDuration: '3s' }}>👼</span>
              <span className="text-sm -mr-2 select-none animate-bounce" style={{ filter: `drop-shadow(0 0 5px ${tc1 || '#fbbf24'})`, animationDuration: '3s', animationDelay: '0.5s' }}>👼</span>
            </div>
          )}

          {/* Glowing Runic Ring Frame Effect */}
          {customization.avatarDecoration === "Runic Ring" && (
            <div
              className="absolute inset-[-4px] rounded-3xl border-2 border-purple-500/30 animate-pulse pointer-events-none z-20 flex items-center justify-center"
              style={{
                boxShadow: `0 0 12px ${tc1 || '#a855f7'}`
              }}
            >
              <div className="text-[6px] text-purple-300 font-mono tracking-tighter opacity-80 select-none">ᛦ᛫ᚠ᛫ᚢ᛫ᚦ</div>
            </div>
          )}

          {/* ==== Section 5: NEW avatar frames ==== */}

          {/* Cat Ears — Kawaii */}
          {customization.avatarDecoration === "Cat Ears" && (
            <>
              <span className="absolute -top-3 left-1.5 text-base pointer-events-none z-30 select-none animate-pulse" style={{ filter: `drop-shadow(0 0 4px ${tc1 || '#f9a8d4'})`, transform: 'rotate(-12deg)' }}>🐱</span>
              <span className="absolute -top-3 right-1.5 text-base pointer-events-none z-30 select-none animate-pulse" style={{ filter: `drop-shadow(0 0 4px ${tc1 || '#f9a8d4'})`, transform: 'rotate(12deg)' }}>🐱</span>
            </>
          )}

          {/* Halo — Mystical */}
          {customization.avatarDecoration === "Halo" && (
            <div
              className="absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-5 pointer-events-none z-30 rounded-full"
              style={{
                background: `radial-gradient(ellipse, ${tc2 || '#fde68a'} 0%, ${tc1 || '#fde68a'}40 40%, transparent 70%)`,
                animation: 'aura-breathe 4s ease-in-out infinite'
              }}
            />
          )}

          {/* Flower Crown — Kawaii */}
          {customization.avatarDecoration === "Flower Crown" && (
            <div className="absolute -top-2 left-0 right-0 flex justify-around pointer-events-none z-30 select-none">
              <span className="text-sm animate-bounce" style={{ filter: `drop-shadow(0 0 3px ${tc1 || '#fbcfe8'})`, animationDuration: '3.2s' }}>🌸</span>
              <span className="text-sm animate-bounce" style={{ filter: `drop-shadow(0 0 3px ${tc2 || '#fde68a'})`, animationDuration: '3.8s', animationDelay: '0.3s' }}>🌼</span>
              <span className="text-sm animate-bounce" style={{ filter: `drop-shadow(0 0 3px ${tc1 || '#fbcfe8'})`, animationDuration: '3.4s', animationDelay: '0.6s' }}>🌷</span>
            </div>
          )}

          {/* Pulsing Aura — Psychedelic */}
          {customization.avatarDecoration === "Pulsing Aura" && (
            <>
              <div
                className="absolute inset-[-6px] rounded-full pointer-events-none z-20 opacity-80"
                style={{
                  background: `radial-gradient(circle, ${tc1 || '#ff10f0'}50 0%, ${tc2 || '#10ffd0'}30 60%, transparent 80%)`,
                  animation: 'aura-pulse 1.6s ease-in-out infinite'
                }}
              />
              <div className="absolute inset-[-4px] rounded-3xl border-2 pointer-events-none z-20" style={{ borderColor: tc1, boxShadow: `0 0 12px ${tc1}` }} />
            </>
          )}

          {/* Rainbow Ring — Psychedelic */}
          {customization.avatarDecoration === "Rainbow Ring" && (
            <div
              className="absolute inset-[-5px] rounded-3xl pointer-events-none z-20"
              style={{
                background: 'conic-gradient(from 0deg, #ff0080, #ff8c00, #ffd700, #10ff80, #10ffd0, #4080ff, #a040ff, #ff0080)',
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                padding: '2px',
                animation: 'aura-rgb-cycle 6s linear infinite'
              }}
            />
          )}

          {/* Dragon Scales — Mystical */}
          {customization.avatarDecoration === "Dragon Scales" && (
            <div
              className="absolute inset-[-4px] rounded-3xl pointer-events-none z-20"
              style={{
                background: `repeating-linear-gradient(45deg, ${tc1 || '#dc2626'}40, ${tc1 || '#dc2626'}40 4px, ${tc2 || '#f59e0b'}40 4px, ${tc2 || '#f59e0b'}40 8px)`,
                boxShadow: `0 0 18px ${tc1 || '#dc2626'}80, inset 0 0 12px ${tc2 || '#f59e0b'}`,
                animation: 'aura-breathe 5s ease-in-out infinite'
              }}
            />
          )}
        </div>
        </div>

      <div className="pt-16 p-8 relative z-20">
        {/* Name and Pronouns */}
        <IdentityRenderer 
          user={profile} 
          isLightPalette={isLightPalette}
          containerClassName="flex items-baseline gap-3 mb-1"
          nameClassName="font-space text-2xl font-black uppercase tracking-tight truncate"
          pronounClassName="text-xs font-semibold uppercase tracking-wider"
        />
        
        {customization.customStatus && (
          <p className="text-xs text-white/50 tracking-wide mb-2 break-words whitespace-pre-wrap">
            {customization.customStatus}
          </p>
        )}
        
        {/* Status Message */}
        {customization.statusMessage && (
          <p className="text-emerald-500 text-xs font-semibold italic mb-3 break-words whitespace-pre-wrap">
            "{customization.statusMessage}"
          </p>
        )}
        
        <p className="text-sm leading-relaxed mb-4 break-words whitespace-pre-wrap" style={{ color: mutedTextColor }}>
          {profile.bio || "This user hasn't set a bio yet."}
        </p>

        {/* Badges Section */}
        <div className="flex items-center gap-2 mb-4">
          {/* Active Call Badge */}
          {profile.inCall && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--color-cyan)]/10 border border-[var(--color-cyan)]/20 text-[10px] text-[var(--color-cyan)] font-black uppercase tracking-widest animate-pulse">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--color-cyan)] shadow-[0_0_8px_var(--color-cyan)]" />
              Active in {profile.roomName}
            </div>
          )}

          {/* Server Roles Section — real roles from DB */}
          {serverRoles.length > 0 && !rolesLoading && (
            <div className="flex flex-wrap gap-2 mt-4 w-full">
              <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest w-full mb-1">
                {profile.id === 'me' ? 'Your Roles' : 'Roles'}
              </span>
              {serverRoles.map((role: any) => (
                <div
                  key={role.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border max-w-full"
                  style={{
                    background: `${role.color || '#ffffff'}20`,
                    borderColor: `${role.color || '#ffffff'}60`,
                    color: role.color || '#ffffff',
                    boxShadow: `0 0 10px ${role.color || '#ffffff'}40`
                  }}
                  title={role.name}
                >
                  {role.icon && <span className="text-xs shrink-0">{role.icon}</span>}
                  {role.role_kind === 'cosmetic' ? (
                    <span className="truncate opacity-80">{role.name}</span>
                  ) : (
                    <span className="truncate">{role.name}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Send DM Button — positioned in bottom-left corner */}
      {onSendDM && !isSelf && (
        <button
          onClick={onSendDM}
          className="absolute bottom-4 left-4 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-black text-xs rounded-xl uppercase tracking-widest transition-all border border-white/10 cursor-pointer z-30"
        >
          💬 Send DM
        </button>
      )}

      {/* Add Friend Button — positioned in bottom-right corner */}
      {onAddFriend && !isSelf && (
        <button
          onClick={onAddFriend}
          className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 bg-[var(--neon)] text-black font-black text-xs rounded-xl uppercase tracking-widest hover:scale-105 transition-all shadow-[0_0_15px_var(--neon)] cursor-pointer z-30"
        >
          <UserPlus size={14} />
          {isFriend ? 'Friends' : 'Add Friend'}
        </button>
      )}
      </div>{/* end color modulation wrapper */}
    </div>
  );
}
